import { useMemo } from "react";
import { getTimeZones, TimeZone } from "@vvo/tzdb";
import { useFetchUserCircles } from "@core/features/circle";
import { makeTimeslotFromId } from "@core/features/timeslots";
import { makeTimezoneOptionFromTzdbTimezone } from "@core/features/timezones";
import { usePlatformStore } from "@platform/store";
import { useProgram } from "@platform/hooks/useProgram";

export const useCircleTimeslot = () => {
  const { program } = useProgram();
  const user = usePlatformStore((s) => s.user);
  const { data: circlesData } = useFetchUserCircles({ user });
  const circle = circlesData?.circles?.[0];

  // Get user timezone option from user's timezone string
  const userTimezoneOption = useMemo(() => {
    if (!user?.timezone) return null;
    const timezones = getTimeZones({ includeUtc: true });
    const foundTimezone = timezones.find((z) => {
      const isUtcGroup = z.name === "Etc/UTC" || z.group.includes("Etc/UTC");
      const value = isUtcGroup ? "UTC" : z.name;
      return value === user.timezone;
    });
    if (!foundTimezone) return null;
    return makeTimezoneOptionFromTzdbTimezone(foundTimezone as TimeZone);
  }, [user?.timezone]);

  const startTimeslot = useMemo(() => {
    if (
      !circle?.start_time_slot ||
      !userTimezoneOption ||
      !program?.scheduled_engagement_start_date
    )
      return null;

    if (typeof circle?.start_time_slot !== "number") return null;

    return makeTimeslotFromId(
      circle?.start_time_slot,
      program?.scheduled_engagement_start_date,
      userTimezoneOption
    );
  }, [circle?.start_time_slot, userTimezoneOption, program?.scheduled_engagement_start_date]);

  const endTimeslot = useMemo(() => {
    if (
      !circle?.end_time_slot ||
      !userTimezoneOption ||
      !program?.scheduled_engagement_start_date
    )
      return null;

    if (typeof circle?.end_time_slot !== "number") return null;

    return makeTimeslotFromId(
      circle?.end_time_slot,
      program?.scheduled_engagement_start_date,
      userTimezoneOption
    );
  }, [circle?.end_time_slot, userTimezoneOption, program?.scheduled_engagement_start_date]);

  return { startTimeslot, endTimeslot };
};

export default useCircleTimeslot;

